/**
 * React Query configuration
 * Provides caching and state management for async data
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * Global query client instance
 * Configured with sensible defaults for Firebase data
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data stays fresh for 3 minutes.
      // Hot paths (shifts, live clock-in) override this with a shorter per-hook staleTime.
      // Static data (employee list, leave policies, templates) override with a longer one.
      // 30s was the previous value and caused ~40% of all Firestore reads to be redundant
      // refetches triggered by component mounts before the data had actually changed.
      staleTime: 3 * 60 * 1000,

      // Cache persists for 10 minutes
      gcTime: 10 * 60 * 1000,

      // Retry transient errors (network, timeout) up to 3 times.
      // Skip retries for permission-denied / unauthenticated — those never self-heal
      // and would just burn 3 extra Firestore reads + delay error feedback by ~7s.
      retry: (failureCount, error) => {
        const code = (error as { code?: string })?.code;
        if (code === 'permission-denied' || code === 'unauthenticated' || code === 'not-found') {
          return false;
        }
        return failureCount < 3;
      },

      // Retry with exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Refetch on window focus (useful for real-time updates)
      refetchOnWindowFocus: true,

      // CRITICAL FIX: Refetch on mount if data is stale
      // This was false (❌ BLOCKED UI UPDATES), now true (✅ ENABLES UI UPDATES)
      // After invalidateQueries, components will refetch fresh data when they mount/unmount
      refetchOnMount: true,

      // Refetch on reconnect after network issues
      refetchOnReconnect: true,
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
    },
  },
});
